import React, { useRef, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { useFlash } from "@/components/hooks/useFlash";
import { Tooltip } from "@/components/common/Tooltip";
import { centsFrom, dollarsText, usd } from "./app-format";

/** How a figure reads, is typed and is shown while it is typed. */
export interface FigureFormat {
  /** What the figure reads as, e.g. "$3.000" or "20 min". */
  show: (value: number) => string;
  /** The value typed, or `null` for something that is not one. */
  parse: (text: string) => number | null;
  /** The field's text for a value. */
  edit: (value: number) => string;
  /** Beside the field while typing: "$" before, "min" after. */
  before?: string;
  after?: string;
  inputMode?: "decimal" | "numeric" | "text";
  /** Ends the field's accessible name, e.g. "in US dollars". */
  unit?: string;
  /** The refusal for text that does not parse. */
  invalid?: string;
}

const MONEY: FigureFormat = {
  show: usd,
  parse: centsFrom,
  edit: dollarsText,
  before: "$",
  inputMode: "decimal",
  unit: "in US dollars",
  invalid: "Not an amount",
};

/** A figure edited where it stands, money in cents unless `format` says
 *  otherwise: it brightens on hover, a click turns it into a field, Enter or
 *  leaving saves, Escape puts it back. With `clearable`, an empty field saves
 *  `null`, which reads as `fallback` if there is one, else "unlimited". */
export function InlineFigure({
  value,
  onSave,
  label,
  format = MONEY,
  prefix,
  hint,
  clearable = false,
  fallback = null,
  refuse,
}: {
  value: number | null;
  onSave: (value: number | null) => Promise<unknown>;
  label: string;
  format?: FigureFormat;
  prefix?: string;
  /** Shown on hover. */
  hint?: string;
  clearable?: boolean;
  fallback?: number | null;
  refuse?: (value: number) => string | null;
}) {
  const [text, setText] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Chrome blurs a field as it leaves the page, which would save what Escape threw away.
  const closed = useRef(false);
  const [saved, flashSaved] = useFlash<true>(1600);
  const cleared = clearable && text !== null && text.trim() === "";
  const typed = text === null || cleared ? null : format.parse(text);
  const refusal =
    text === null || cleared
      ? null
      : typed === null
        ? text.trim()
          ? (format.invalid ?? "Not a value")
          : null
        : (refuse?.(typed) ?? null);
  const close = () => {
    closed.current = true;
    setText(null);
    setError(null);
  };
  const submit = (leaving: boolean) => {
    if (closed.current || pending) return;
    const next = cleared ? null : typed;
    if (refusal || (next === null && !cleared))
      return leaving ? close() : undefined;
    if (next === value) return close();
    setPending(true);
    onSave(next)
      .then(() => {
        close();
        flashSaved(true);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setPending(false));
  };
  const lead = prefix && <span>{prefix}</span>;

  if (text === null) {
    const shown = value ?? fallback;
    return (
      <>
        <Tooltip title={hint}>
          {/* eslint-disable-next-line no-restricted-syntax -- inline text: it takes the line's type, which Button replaces */}
          <button
            type="button"
            onClick={() => {
              closed.current = false;
              setText(value === null ? "" : format.edit(value));
            }}
            aria-label={`Change the ${label}`}
            className="group inline-flex items-baseline gap-1.5 cursor-text text-inherit outline-none"
          >
            {lead}
            <span className="font-mono underline decoration-dotted decoration-border underline-offset-4 group-hover:text-fg group-hover:decoration-accent group-focus-visible:text-fg group-focus-visible:decoration-accent">
              {shown === null ? "unlimited" : format.show(shown)}
            </span>
          </button>
        </Tooltip>
        {saved && (
          <CheckIcon
            sx={{ fontSize: 12, color: "var(--color-success)" }}
            className="self-center"
            aria-label="Saved"
          />
        )}
      </>
    );
  }
  const shownRefusal = refusal ?? error;
  const name = `${label[0].toUpperCase()}${label.slice(1)}`;
  return (
    <span className="inline-flex items-baseline gap-3">
      <span className="inline-flex items-baseline gap-1.5">
        {lead}
        <span
          className={`font-mono text-fg ${shownRefusal ? "shadow-[inset_0_-1px_0_var(--color-error)]" : "shadow-[inset_0_-1px_0_var(--color-accent)]"} ${pending ? "opacity-50" : ""}`}
        >
          {format.before}
          <input
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setError(null);
            }}
            // The caret lands after the amount, ready to edit it, not to replace it.
            onFocus={(e) => {
              const end = e.target.value.length;
              e.target.setSelectionRange(end, end);
            }}
            onBlur={() => submit(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit(false);
              if (e.key === "Escape") close();
            }}
            autoFocus
            readOnly={pending}
            inputMode={format.inputMode ?? "text"}
            placeholder={clearable ? "unlimited" : undefined}
            aria-label={format.unit ? `${name} ${format.unit}` : name}
            aria-invalid={Boolean(shownRefusal)}
            className="bg-transparent p-0 border-0 outline-none"
            style={{
              font: "inherit",
              color: "inherit",
              width: `calc(${Math.max(text.length || (clearable ? 9 : 1), 1)}ch + 1px)`,
            }}
          />
          {format.after && ` ${format.after}`}
        </span>
      </span>
      {shownRefusal && (
        <span style={{ color: "var(--color-error)" }}>{shownRefusal}</span>
      )}
    </span>
  );
}
